import React, { useMemo } from 'react';
import { isFinite, sumBy } from 'lodash';
import Tooltip from '@lendi-ui/tooltip';

import { formatNumberToStringPrecision, maskPIIFullStory } from '@funnels/utils';

import { NumeralDisplayProps } from '../../Fields/PlainDisplay/index';
import Stack from '../../Stack';
import { DEFAULT_VALUE } from './constants';
import {
  AmountText,
  Container,
  Divider,
  ItemHeading,
  ItemWrapper,
  SpacedGridUnit,
  SummaryHeading,
  SummaryItemWrapper,
  TotalAmountText,
} from './index.style';
import { Item } from './types';

interface SummaryProps {
  headTextValue?: string;
  title: string;
  before?: string;
  after?: string;
  items: Item[];
  shouldMaskItemLabel?: boolean;
}

interface SummaryItemProps extends NumeralDisplayProps {
  titleBefore?: React.ReactNode;
  shouldMaskTitle?: boolean;
}

function SummaryItem(props: SummaryItemProps) {
  const title = props.title;
  const amountText = `${props.before || ''}${
    isFinite(props.value) ? formatNumberToStringPrecision(props.value, 0) : DEFAULT_VALUE
  }${props.after || ''}`;

  return (
    <SummaryItemWrapper>
      <Tooltip content={title} position="right">
        <ItemHeading data-component={'summary-card-item-heading'}>
          {props.titleBefore}
          {title && props.shouldMaskTitle ? maskPIIFullStory(title) : title}
        </ItemHeading>
      </Tooltip>
      <Tooltip content={amountText} position="right">
        <AmountText data-component={'summary-card-item-content'}>{amountText}</AmountText>
      </Tooltip>
    </SummaryItemWrapper>
  );
}
interface SummaryTotalProps extends NumeralDisplayProps {
  textValue?: string;
}

function SummaryTotal(props: SummaryTotalProps) {
  let headValue = isFinite(props.value) ? formatNumberToStringPrecision(props.value, 0) : DEFAULT_VALUE;
  headValue = props.textValue ? props.textValue : headValue;

  return (
    <div>
      <SummaryHeading>{props.title}</SummaryHeading>
      <TotalAmountText>{`${props.before || ''}${headValue}${props.after || ''}`}</TotalAmountText>
    </div>
  );
}

function SummaryCard(props: SummaryProps) {
  const { title, items, before, headTextValue, shouldMaskItemLabel } = props;
  const totalAmount = useMemo(() => sumBy(items, 'value'), [items]);

  // 1-4 applicants will stay horizontally 5+ will have 3 in each row
  const unitSize = items.length === 4 ? 1 / 4 : 1 / 3;

  return (
    <Container data-component={'summary-card'}>
      <Stack halign="justify">
        <SpacedGridUnit>
          <SummaryTotal before={before} title={title} value={totalAmount} textValue={headTextValue} />
        </SpacedGridUnit>
      </Stack>
      <Divider />
      <Stack halign="left">
        {items
          .filter((item) => item.label)
          .map((item, index) => (
            <SpacedGridUnit key={index} size={unitSize} data-component={'summary-card-unit'}>
              <ItemWrapper>
                <SummaryItem
                  before={before}
                  title={item.label}
                  value={item.value}
                  titleBefore={item.titleBefore}
                  shouldMaskTitle={shouldMaskItemLabel}
                />
              </ItemWrapper>
            </SpacedGridUnit>
          ))}
      </Stack>
    </Container>
  );
}

export default SummaryCard;
